import React, { useEffect, useState } from 'react';
import {
    CAlert,
    CButton,
    CCol,
    CFormInput,
    CFormLabel,
    CFormTextarea,
    CModal,
    CModalBody,
    CModalFooter,
    CModalHeader,
    CModalTitle,
    CRow,
    CSpinner,
} from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import { cilCloudUpload } from '@coreui/icons';
import * as DomicilappService from '../../services/DomicilappService';
import { useNotification } from '../../contexts/NotificationContext';
import { AdUpdatePayload, SERVICE_FLASH, SERVICE_PUSH, StoreAd } from '../../types/marketing';
import { formatCOP, toInputDate } from './marketingUtils';

interface Props {
    storeId: string;
    ad: StoreAd | null;
    balance: number;
    onClose: () => void;
    onSaved: () => void;
}

const toBackendDate = (value: string) => (value ? value.replace('T', ' ') + ':00' : '');

/**
 * Edita una campaña en curso: creativo (textos e imagen), fecha de fin, presupuesto adicional y hora del push
 * si aún no se envió. El canal, el contenido anunciado y la audiencia no cambian (para eso se termina y se crea otra).
 */
const AdEditModal: React.FC<Props> = ({ storeId, ad, balance, onClose, onSaved }) => {
    const { showToast } = useNotification();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [buttonText, setButtonText] = useState('');
    const [conditionsText, setConditionsText] = useState('');
    const [endDate, setEndDate] = useState('');
    const [addBudget, setAddBudget] = useState<number | ''>('');
    const [pushSchedule, setPushSchedule] = useState('');
    const [image, setImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const display = ad?.channels.find((c) => c.service_id !== SERVICE_PUSH);
    const push = ad?.channels.find((c) => c.service_id === SERVICE_PUSH);
    const pushPending = !!push && !push.sent_at;

    useEffect(() => {
        if (!ad) return;
        setTitle(ad.title);
        setDescription(ad.description ?? '');
        setButtonText(ad.button_text ?? '');
        setConditionsText(ad.conditions_text ?? '');
        setEndDate(toInputDate(ad.end_date));
        setAddBudget('');
        setPushSchedule(push?.schedule_date ? toInputDate(push.schedule_date) : '');
        setImage(null);
        setImagePreview(ad.image_url || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ad]);

    const handleImage = (file: File | null) => {
        if (image && imagePreview) URL.revokeObjectURL(imagePreview);
        setImage(file);
        setImagePreview(file ? URL.createObjectURL(file) : ad?.image_url || null);
    };

    const extra = Number(addBudget) || 0;

    const handleSave = () => {
        if (!ad) return;
        const payload: AdUpdatePayload = {
            title: title.trim(),
            description,
            button_text: buttonText,
            conditions_text: conditionsText,
            end_date: toBackendDate(endDate),
            add_budget: extra,
            push_schedule_date: pushPending && pushSchedule ? toBackendDate(pushSchedule) : null,
        };
        const fd = new FormData();
        fd.append('data', JSON.stringify(payload));
        if (image) fd.append('image', image, image.name);

        setSaving(true);
        DomicilappService.updateStoreAd(storeId, ad.id, fd)
            .then((response: any) => {
                if (response?.status === 200) {
                    showToast({
                        title: 'Campaña actualizada',
                        message: extra > 0 ? `Se descontaron ${formatCOP(extra)} de tu saldo` : 'Los cambios ya se ven en la app',
                        type: 'success',
                        delay: 3500,
                    });
                    onSaved();
                } else {
                    showToast({ title: 'No se pudo guardar', message: typeof response?.data?.error === 'string' ? response.data.error : response?.message, type: 'warning', delay: 4500 });
                }
            })
            .catch(() => showToast({ title: '¡Ha ocurrido un error!', message: 'No fue posible guardar la campaña', type: 'danger', delay: 3500 }))
            .finally(() => setSaving(false));
    };

    return (
        <CModal visible={!!ad} onClose={onClose} size="lg" alignment="center" backdrop="static">
            <CModalHeader>
                <CModalTitle>Editar campaña #{ad?.id}</CModalTitle>
            </CModalHeader>
            <CModalBody>
                <CRow className="g-3">
                    <CCol md={8}>
                        <CFormLabel className="small fw-semibold">Título</CFormLabel>
                        <CFormInput value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} />
                    </CCol>
                    <CCol md={4}>
                        <CFormLabel className="small fw-semibold">Texto del botón</CFormLabel>
                        <CFormInput value={buttonText} maxLength={40} onChange={(e) => setButtonText(e.target.value)} />
                    </CCol>
                    <CCol md={12}>
                        <CFormLabel className="small fw-semibold">Descripción</CFormLabel>
                        <CFormTextarea rows={2} maxLength={255} value={description} onChange={(e) => setDescription(e.target.value)} />
                    </CCol>
                    <CCol md={8}>
                        <CFormLabel className="small fw-semibold">Condiciones</CFormLabel>
                        <CFormInput value={conditionsText} maxLength={120} onChange={(e) => setConditionsText(e.target.value)} />
                    </CCol>
                    <CCol md={4}>
                        <CFormLabel className="small fw-semibold">Se muestra hasta</CFormLabel>
                        <CFormInput type="datetime-local" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </CCol>
                    <CCol md={12}>
                        <label
                            htmlFor="ad-edit-image-input"
                            className="d-flex align-items-center justify-content-center border rounded-3 bg-body-tertiary overflow-hidden"
                            style={{ borderStyle: 'dashed', minHeight: '120px', cursor: 'pointer' }}
                        >
                            {imagePreview ? (
                                <img src={imagePreview} alt="" style={{ maxHeight: '170px', maxWidth: '100%', objectFit: 'cover' }} />
                            ) : (
                                <span className="text-body-secondary small d-flex align-items-center gap-2">
                                    <CIcon icon={cilCloudUpload} size="lg" /> Cambiar imagen
                                </span>
                            )}
                            <input id="ad-edit-image-input" type="file" accept="image/*" className="d-none" onChange={(e) => handleImage(e.target.files?.[0] ?? null)} />
                        </label>
                        <small className="text-body-secondary">Toca la imagen para cambiarla.</small>
                    </CCol>

                    {display && ad?.budget !== null && ad && (
                        <CCol md={6}>
                            <CFormLabel className="small fw-semibold">Agregar presupuesto</CFormLabel>
                            <CFormInput type="number" min={0} step={1000} placeholder="$0" value={addBudget} onChange={(e) => setAddBudget(e.target.value === '' ? '' : Number(e.target.value))} />
                            <small className="text-body-secondary">
                                Consumido {formatCOP(ad.spent)} de {formatCOP(ad.budget)} · tu saldo {formatCOP(balance)}
                            </small>
                        </CCol>
                    )}
                    {pushPending && (
                        <CCol md={6}>
                            <CFormLabel className="small fw-semibold">Enviar la notificación</CFormLabel>
                            <CFormInput type="datetime-local" value={pushSchedule} onChange={(e) => setPushSchedule(e.target.value)} />
                            <small className="text-body-secondary">Aún no se ha enviado; el texto será el título y la descripción de arriba.</small>
                        </CCol>
                    )}
                </CRow>

                {display?.service_id === SERVICE_FLASH && (
                    <CAlert color="secondary" className="small mt-3 mb-0 bg-body-tertiary text-body-secondary border">
                        El flash message se muestra una sola vez por cliente: quienes ya lo vieron no verán el cambio.
                    </CAlert>
                )}
                {extra > balance && (
                    <CAlert color="warning" className="small mt-3 mb-0">Saldo insuficiente: te faltan {formatCOP(extra - balance)}.</CAlert>
                )}
            </CModalBody>
            <CModalFooter>
                <CButton color="light" onClick={onClose}>Cancelar</CButton>
                <CButton color="dark" disabled={saving || !title.trim() || extra > balance} onClick={handleSave}>
                    {saving && <CSpinner size="sm" className="me-1" />}
                    {extra > 0 ? `Guardar y pagar ${formatCOP(extra)}` : 'Guardar cambios'}
                </CButton>
            </CModalFooter>
        </CModal>
    );
};

export default AdEditModal;
